import clsx from 'clsx'
import NextImage, { ImageProps } from 'next/image'
import { ReactEventHandler, useEffect, useState } from 'react'
import { isValidImageUrl } from '~/src/lib/urlHelpers'

interface Props extends Omit<ImageProps, 'alt' | 'priority'> {
  alt?: string
  overlay?: boolean
  rounded?: boolean
  retry?: number
  priority?: boolean
}

const fixUrl = (url: string | undefined | null): string => {
  if (url?.startsWith('data:image') || url?.startsWith('https://data:image')) {
    return url.replace(/https?:\/\//, '')
  }
  if (typeof url !== 'string') return ''
  if (!url) return ''

  if (url.match(/https?:\/\/http?:\/\//)) {
    return url.replace(/https?:\/\/http?:\/\//, 'https://')
  }

  if (url.match(/https?:\/\//)) {
    return url.replace(/https?:\/\//, 'https://')
  } else if (url.match(/https?:\/\//g)) {
    return url.replace(/https?:\/\//g, 'https://')
  }

  return url || ''
}

const FadeImage: React.FC<Props> = ({
  overlay,
  rounded,
  retry = 0,
  className,
  priority,
  ...props
}) => {
  const [retryCount, setRetryCount] = useState(0)
  const [ready, setReady] = useState(false)

  const onLoad: ReactEventHandler<HTMLImageElement> = (event) => {
    event.persist()
    const imageElement = event.target as HTMLImageElement
    if (imageElement.srcset || imageElement.src) {
      setReady(true)
    }
  }

  const onError: ReactEventHandler<HTMLImageElement> = (event) => {
    if (retryCount >= retry) return

    event.persist()
    const imageElement = event.target as HTMLImageElement

    if (retryCount < retry) {
      setRetryCount(retryCount + 1)

      const timestamp = new Date().getTime()
      const srcUrl = new URL(imageElement.src)
      srcUrl.searchParams.set('ts', String(timestamp))
      imageElement.src = srcUrl.toString()

      if (imageElement.srcset) {
        const srcsetUrls = imageElement.srcset.split(',').map((srcsetItem) => {
          const [url, descriptor] = srcsetItem.trim().split(' ')
          if (!url) return srcsetItem.trim()
          const srcsetUrl = new URL(url)
          srcsetUrl.searchParams.set('ts', String(timestamp))
          return `${srcsetUrl.toString()} ${descriptor || ''}`
        })
        imageElement.srcset = srcsetUrls.join(', ')
      }
    }

    if (props?.onError) {
      props.onError(event)
    }

    setReady(true)
  }

  // wait 2.5 seconds after that setReady to true
  useEffect(() => {
    if (ready) return
    const timeout = setTimeout(() => {
      setReady(true)
    }, 2500)

    return () => {
      clearTimeout(timeout)
    }
  }, [ready])

  if (fixUrl(props.src as string) === '') {
    return null
  }

  return (
    <div
      className={clsx('relative w-full transition', {
        'overflow-hidden rounded-md': rounded,
      })}
    >
      {isValidImageUrl(fixUrl(props.src as string)) && (
        <NextImage
          {...props}
          src={fixUrl(props.src as string)}
          alt={props.alt || ''}
          className={clsx(
            'duration-50 inline-block transition-opacity',
            {
              'opacity-100': ready,
              'opacity-0': !ready,
            },
            className
          )}
          onLoad={onLoad}
          onError={onError}
          priority={priority}
        />
      )}
      {overlay && (
        <div className='pointer-events-none absolute inset-0 bg-black/5 bg-blend-multiply dark:bg-white/10' />
      )}
    </div>
  )
}

export default FadeImage
